import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useLazyQuery } from '@apollo/client';
import { useDebounce, useGetCountryFromUrl } from '../../hooks';
import { CompanyType } from '../../propTypes';
import GET_SIMPLE_MASTER_ENTITIES from '../../graphql/queries/get-simple-master-entities';

const CompanyFilter = ({
  selectedCompany,
  setSelectedCompany,
  companyFilterSetProps,
  inputLabel,
  usePlaceholder,
  ...props
}) => {
  const country = useGetCountryFromUrl();

  const [fetchCompanies, { data, loading }] = useLazyQuery(
    GET_SIMPLE_MASTER_ENTITIES,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const debouncedFetch = useDebounce((value) => {
    fetchCompanies({
      variables: {
        globalFilter: value,
        first: 10,
        countryId: country?.id,
        ...companyFilterSetProps,
      },
    });
  }, 500);

  const handleInputChange = (_, newInputValue) => {
    if (!newInputValue.trim()) return;
    debouncedFetch(newInputValue);
  };

  const options = useMemo(
    () => data?.getMasterEntities.edges.map((i) => i.node) || [],
    [data],
  );

  const getOptionLabel = useCallback(
    (option) => `${option?.displayNationalIdentifier} ${option?.name}`,
    [],
  );

  const handleChange = useCallback(
    (_, value) => {
      setSelectedCompany(value);
    },
    [setSelectedCompany],
  );

  const isOptionEqualToValue = useCallback(
    (option, value) => option.id === value.id,
    [],
  );

  return (
    <Autocomplete
      id="select-company"
      loading={loading}
      options={options}
      getOptionLabel={getOptionLabel}
      onChange={handleChange}
      value={selectedCompany}
      isOptionEqualToValue={isOptionEqualToValue}
      onInputChange={handleInputChange}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          {...(usePlaceholder
            ? { placeholder: inputLabel }
            : { label: inputLabel })}
        />
      )}
      {...props}
    />
  );
};

CompanyFilter.propTypes = {
  selectedCompany: CompanyType,
  setSelectedCompany: PropTypes.func.isRequired,
  companyFilterSetProps: PropTypes.shape(),
  inputLabel: PropTypes.string,
  usePlaceholder: PropTypes.bool,
};

CompanyFilter.defaultProps = {
  selectedCompany: null,
  inputLabel: 'Selecciona deudor',
  companyFilterSetProps: {},
  usePlaceholder: false,
};

export default CompanyFilter;
